/**
 * GOVOPS-1008C — GovOps HTTP client for RobinhoodSpace CRA.
 */
import { GOVOPS_DEFAULT_API_BASE } from './govopsConfig';
import { GovOpsEntitlementRequiredError } from './govopsExceptions';

const LOCAL_DEFAULT = 'http://localhost:8080/api/govops';

export class GovOpsApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'GovOpsApiError';
    this.status = status;
    this.body = body;
  }
}

export class GovOpsAuthError extends Error {
  constructor(message) {
    super(message);
    this.name = 'GovOpsAuthError';
    this.message = message;
  }
}

export function resolveGovOpsApiBase() {
  if (typeof window !== 'undefined' && window.GOVOPS_API_BASE) {
    return String(window.GOVOPS_API_BASE).replace(/\/+$/, '');
  }
  if (process.env.REACT_APP_GOVOPS_API_BASE) {
    return String(process.env.REACT_APP_GOVOPS_API_BASE).replace(/\/+$/, '');
  }
  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return LOCAL_DEFAULT;
    }
  }
  return GOVOPS_DEFAULT_API_BASE;
}

function buildUrl(base, path, query) {
  const root = String(base || '').replace(/\/+$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  let url = `${root}${p}`;
  if (!query || typeof query !== 'object') return url;
  const params = new URLSearchParams();
  Object.keys(query).forEach((key) => {
    const val = query[key];
    if (val !== undefined && val !== null && val !== '') params.set(key, String(val));
  });
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function resolveAuthToken(getAuthToken, forceRefresh = false) {
  if (typeof window !== 'undefined' && window.__GOVOPS_AUTH_TOKEN__) {
    return window.__GOVOPS_AUTH_TOKEN__;
  }
  if (process.env.REACT_APP_GOVOPS_AUTH_TOKEN) {
    return process.env.REACT_APP_GOVOPS_AUTH_TOKEN;
  }
  try {
    const { auth } = await import('../firebaseconfig');
    const user = auth?.currentUser;
    if (user && typeof user.getIdToken === 'function') {
      return await user.getIdToken(forceRefresh);
    }
  } catch (_) {
    /* firebase not configured */
  }
  return null;
}

export async function defaultGetAuthToken() {
  return resolveAuthToken(null, false);
}

function mapHttpError(status, json, fallbackText) {
  const msg = (json && (json.message || json.error)) || fallbackText || 'GovOps request failed';
  if (status === 401) return new GovOpsAuthError(msg);
  if (status === 403) return new GovOpsEntitlementRequiredError(msg);
  return new GovOpsApiError(msg, status, json);
}

export function createGovOpsClient(options = {}) {
  const getBaseUrl = options.getBaseUrl || resolveGovOpsApiBase;
  const getAuthToken = options.getAuthToken || defaultGetAuthToken;
  const fetchImpl = options.fetch || fetch;

  async function request(path, opts = {}, retried = false) {
    const method = opts.method || 'GET';
    const headers = { ...(opts.headers || {}) };

    if (opts.body !== undefined && opts.body !== null) {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }

    if (opts.auth !== false) {
      const token =
        opts.__authToken || (await resolveAuthToken(getAuthToken, retried));
      if (!token) {
        throw new GovOpsAuthError('Authentication required (Bearer Firebase ID token)');
      }
      headers.Authorization = `Bearer ${token}`;
    }

    const url = buildUrl(getBaseUrl(), path, opts.query);
    const res = await fetchImpl(url, {
      method,
      headers,
      body:
        opts.body !== undefined && opts.body !== null
          ? typeof opts.body === 'string'
            ? opts.body
            : JSON.stringify(opts.body)
          : undefined,
    });

    if (opts.responseType === 'text') {
      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw mapHttpError(res.status, null, errText || res.statusText);
      }
      return res.text();
    }

    const ct = res.headers.get('content-type') || '';
    let json = null;
    if (ct.includes('application/json') || ct.includes('+json')) {
      json = await res.json().catch(() => null);
    } else if (!res.ok) {
      const plain = await res.text().catch(() => '');
      throw mapHttpError(res.status, null, plain || res.statusText);
    }

    if (!res.ok) {
      if (res.status === 401 && opts.auth !== false && !retried) {
        const fresh = await resolveAuthToken(getAuthToken, true);
        if (fresh) {
          return request(path, { ...opts, __authToken: fresh }, true);
        }
      }
      throw mapHttpError(res.status, json, res.statusText);
    }

    return json;
  }

  return {
    getBaseUrl,
    health: () => request('/health', { auth: false }),
    listPermits: (query) => request('/permits', { query }),
    getPermit: (permitId) => request(`/permits/${encodeURIComponent(permitId)}`),
    createPermit: (body) => request('/permits', { method: 'POST', body }),
    updatePermit: (permitId, body) =>
      request(`/permits/${encodeURIComponent(permitId)}`, { method: 'PATCH', body }),
    exportPermitsCsv: (query) =>
      request('/permits/export.csv', { query, responseType: 'text' }),
    listCases: (query) => request('/cases', { query }),
    getCase: (caseId) => request(`/cases/${encodeURIComponent(caseId)}`),
    createCase: (body) => request('/cases', { method: 'POST', body }),
    updateCase: (caseId, body) =>
      request(`/cases/${encodeURIComponent(caseId)}`, { method: 'PATCH', body }),
    exportCasesCsv: (query) =>
      request('/cases/export.csv', { query, responseType: 'text' }),
    listViolations: (query) => request('/violations', { query }),
    createViolation: (body) => request('/violations', { method: 'POST', body }),
    updateViolation: (violationId, body) =>
      request(`/violations/${encodeURIComponent(violationId)}`, { method: 'PATCH', body }),
    exportViolationsCsv: (query) =>
      request('/violations/export.csv', { query, responseType: 'text' }),
    listAuditEvents: (query) => request('/audit', { query }),
    search: (query) => request('/search', { query }),
    listNotifications: (query) => request('/notifications', { query }),
    listPermitPayments: (permitId) =>
      request(`/permits/${encodeURIComponent(permitId)}/payments`),
    recordPermitPayment: (permitId, body) =>
      request(`/permits/${encodeURIComponent(permitId)}/payment`, { method: 'POST', body }),
    sendRenewalNotice: (permitId, body) =>
      request(`/permits/${encodeURIComponent(permitId)}/renewal-notice`, { method: 'POST', body }),
    renewPermit: (permitId, body) =>
      request(`/permits/${encodeURIComponent(permitId)}/renew`, { method: 'POST', body }),
    fetchMunicipalOperations: (query) => request('/operations/municipal', { query }),
    listVendors: (query) => request('/vendors', { query }),
    createVendor: (body) => request('/vendors', { method: 'POST', body }),
    listVendorPermits: (vendorId, query) =>
      request(`/vendors/${encodeURIComponent(vendorId)}/permits`, { query }),
  };
}

let singleton;
export function getGovOpsClient() {
  if (!singleton) singleton = createGovOpsClient();
  return singleton;
}
